import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, SlidersHorizontalIcon } from 'lucide-react';
import type { Product } from '../../types/product';
import { StarRating } from './StarRating';
import { StockLabel } from './StockLabel';
import { useCart } from '../../contexts/CartContext';
import { formatNumber, formatPrice } from '../../utils/currency';
import { discountPercent, pricePerKwh } from '../../utils/products';
export function ProductCard({
  product
}: {
  product: Product;
}) {
  const {
    addItem
  } = useCart();
  const [wished, setWished] = useState(false);
  const discount = discountPercent(product);
  const href = `/product/${product.id}`;
  const swatches = product.variants?.find(g => g.kind === 'swatch');
  const hasPriceVariants = product.variants?.some(g => g.options.some(o => o.priceDelta > 0));
  const optionHint = product.variants?.find(g => g.kind !== 'swatch');
  const perKwh = pricePerKwh(product);
  return <article className="group flex h-full flex-col rounded-xl border border-line bg-white p-3 transition-[border-color,box-shadow] duration-200 hover:border-ink/20 hover:shadow-[0_10px_28px_-14px_rgba(14,26,36,0.22)]" data-id="element-365" data-mp="4bfufzympmbx">
      <div className="relative aspect-square overflow-hidden rounded-lg bg-surface" data-id="element-366" data-mp="3rflx6k28x2aa">
        <Link to={href} tabIndex={-1} aria-hidden="true" data-id="element-367" data-mp="3mhale3se4hpf">
          <img src={product.image} alt="" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-368" data-mp="3mivhvyy9mapx" />
        </Link>
        <div className="pointer-events-none absolute left-2.5 top-2.5 flex gap-1.5" data-id="element-369" data-mp="j2ewst8vfen2">
          {discount > 0 && <span className="rounded-md bg-sale px-2 py-1 text-[11px] font-bold text-white" data-id="element-370" data-mp="e7iwl5thdkyv">−{discount}%</span>}
          {product.badge && <span className={`rounded-md px-2 py-1 text-[11px] font-bold ${product.badge === 'Nieuw' ? 'bg-volt text-ink' : 'bg-ink text-white'}`} data-id="element-371" data-mp="16q6aa065dxpx">
              {product.badge}
            </span>}
        </div>
        <button type="button" onClick={() => setWished(w => !w)} aria-pressed={wished} aria-label={wished ? `${product.name} verwijderen uit verlanglijst` : `${product.name} toevoegen aan verlanglijst`} className="absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-white text-ink transition-transform duration-150 active:scale-90" data-id="element-372" data-mp="2w8bjju3lg9bm">
          <HeartIcon className={`h-4 w-4 ${wished ? 'fill-sale text-sale' : ''}`} data-id="element-373" data-mp="2cl5i9rzdi5s1" />
        </button>
      </div>

      <div className="flex flex-1 flex-col px-1 pt-3.5" data-id="element-374" data-mp="2hfrkb4jkelzg">
        <div className="flex items-center justify-between gap-2" data-id="element-375" data-mp="23jo3qe1m6c5g">
          <p className="truncate text-xs font-medium text-muted" data-id="element-376" data-mp="28gxq1ktpi7p2">{product.brand}</p>
          {swatches && <span className="flex gap-1" aria-label={`${swatches.options.length} kleuren`} data-id="element-377" data-mp="1qq5g2p2ird4">
              {swatches.options.map(o => <span key={o.id} className="h-3 w-3 rounded-full ring-1 ring-ink/15" style={{
            backgroundColor: o.swatch
          }} data-id="element-378" data-mp="6pb6g4c59f2m" />)}
            </span>}
        </div>
        <h3 className="mt-1 line-clamp-2 min-h-[2.5rem] text-[15px] font-semibold leading-5 text-ink" data-id="element-379" data-mp="bw809bmopf3x">
          <Link to={href} className="hover:underline" data-id="element-380" data-mp="pnw8h3ttbjfm">
            {product.name}
          </Link>
        </h3>

        <ul className="mt-2.5 flex gap-1.5 overflow-hidden" aria-label="Belangrijkste specificaties" data-id="element-381" data-mp="kubju2e401t3">
          {product.specs.map(s => <li key={s} className="shrink-0 rounded-md bg-surface px-2 py-1 text-xs font-medium text-ink" data-id="element-382" data-mp="ulxqdlfi5cpi">
              {s}
            </li>)}
        </ul>

        <div className="mt-2.5 flex items-center gap-1.5" data-id="element-383" data-mp="18j31w0xzj3s5">
          <StarRating rating={product.rating} data-id="element-384" data-mp="14kn7hbzkqt4p" />
          <span className="text-xs text-muted" data-id="element-385" data-mp="14hr8rlfwk0f7">
            {formatNumber(product.rating)} ({formatNumber(product.reviews)})
          </span>
        </div>

        <div className="mt-auto pt-4" data-id="element-386" data-mp="36ca35quevc7i">
          <p className="h-5 truncate text-[11px] font-medium text-muted" data-id="element-387" data-mp="36dbhq5amygtz">
            {perKwh ? <span className="rounded bg-volt/40 px-1.5 py-0.5 font-semibold text-ink" data-id="element-388" data-mp="37ugxjmi7shdp">{formatPrice(perKwh)} per kWh</span> : optionHint ? `${optionHint.options.length} opties voor ${optionHint.name.toLowerCase()}` : ''}
          </p>
          <div className="flex items-baseline gap-2" data-id="element-389" data-mp="2p1mr5gynpxl3">
            <span className="text-xl font-bold tracking-tight text-ink" data-id="element-390" data-mp="2p37kj398jahd">
              {hasPriceVariants && <span className="mr-1 text-xs font-medium text-muted" data-id="element-391" data-mp="2t4197yui4tbb">vanaf</span>}
              {formatPrice(product.price)}
            </span>
            {product.compareAt && <span className="text-sm text-muted line-through" data-id="element-392" data-mp="32tksh5m4mp6p">{formatPrice(product.compareAt)}</span>}
          </div>
          <div className="mt-1" data-id="element-393" data-mp="25cm1fqjyhjso">
            <StockLabel status={product.stock} data-id="element-394" data-mp="25ddmw8jua158" />
          </div>
          {product.template === 'system' ? <Link to={href} className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-ink bg-white text-sm font-semibold text-ink transition-colors duration-150 hover:bg-ink hover:text-white" data-id="element-395" data-mp="2aanik5xtn91i">
              <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-396" data-mp="1stqchh85fsrn" />
              Samenstellen
            </Link> : <button type="button" onClick={() => addItem(product)} className="mt-3 h-10 w-full whitespace-nowrap rounded-lg bg-ink text-sm font-semibold text-white transition-[background-color,transform] duration-150 hover:bg-ink-soft active:scale-[0.98]" data-id="element-397" data-mp="1ovsm4b2n70mo">
              {product.stock === 'preorder' ? 'Pre-order' : 'In winkelwagen'}
            </button>}
        </div>
      </div>
    </article>;
}